import UniversalLoader from 'oc-threejs/loader/UniversalLoader.js';
import revertThreejsConstants from 'oc-threejs/revertThreejsConstants.js';
import Scenes from 'oc-threejs/Scenes.js';
import Textures from 'oc-threejs/Textures.js';
import Commons from 'oc-commons/Commons.js';
import AbstractMaterialFactory from "oc-threejs/loader/AbstractMaterialFactory.js";
import Images from 'oc-canvas2d/Images.js';
import {v2, v3} from "oc-threejs/short3";
import Urls from "oc-commons/Urls";
import Maths3 from "oc-threejs/Maths3";
import Models from "../Models";
import CircleUvBufferGeometry from "oc-threejs/geometries/CircleUvBufferGeometry";
import Maths from "oc-commons/Maths";




/**
 * SceneWkfg2Importer erzeugt die Lichtquellen und dient als MaterialFactory.
 * Dabei werden die Tabellen des Backends herangezogen.
 * @example
 * Commons.delayUntil(()=>{
 *    const sceneImporter = new SceneWkfg2Importer(APP.settings.wkfgContentBaseUrl);
 *    UniversalLoader.setMaterialFactory(sceneImporter);
 *    const lightSetting = this.settings.lowPerformanceDevice ? 'low' : 'default';
 *    sceneImporter.createLights(lightSetting);
 * },()=>!Commons.isEmpty(APP.metadata) && !Commons.isEmpty(APP.metamodel));
 */
export default class SceneWkfg2Importer extends AbstractMaterialFactory {

    /**
     * @param texture_root
     */
    constructor(texture_root) {
        super(texture_root, null);
        this.textureCache = new Map();
        this.useLowResTextures = false;
        this.useLambertInsteadOfPhong = false;
        this.useMinAnisotropy = false;

        // this.pmremGenerator = new THREE.PMREMGenerator(STAGE.renderer);
        // this.pmremGenerator.compileEquirectangularShader();
    }

    /** */
    getAllKnownMaterialNames() {
        const names = new Set();
        APP.metadata.Material.forEach(row => names.add(row.name));
        APP.metadata.MaterialPbr.forEach(row => names.add(row.name));
        return names;
    }

    /**
     * @param settingName
     */
    createLights(settingName = null) {
        if (Commons.isEmpty(APP.metadata.Light)) {
            return [];
        }
        Scenes.removeLights();
        let lightGroupName  = "LightGroup";
        let cameraLightGroupName = "CameraLightGroup";
        let settingRows = APP.metadata.Light;
        let lights = [];
        if (settingName) {
            lightGroupName += ':' + settingName;
            cameraLightGroupName += ':' + settingName;
            // aus historischen Gründen leider CaseInsensitiver Vergleich :(
            const settingNameLowerCase = settingName.toLowerCase();
            settingRows = settingRows.filter(row => (row.Setting||'').toLowerCase() == settingNameLowerCase);
        }

        const lightgroup = scene.getObjectByName(lightGroupName) || new THREE.Object3D();
        lightgroup.name = lightGroupName;
        lightgroup.raycastVisibleTags = [];
        const cameraLightGroup = scene.getObjectByName(cameraLightGroupName) || new THREE.Object3D();
        cameraLightGroup.name = cameraLightGroupName;

        const vectorBySelector = selector => {
            let p = Scenes.findBySelector(STAGE.scene, selector);
            if (p==null) {
                p = v3();
            } else if (p instanceof THREE.Object3D) {
                p = p.position.clone();
            }
            // umgeht NAN Bugs
            if (p.distanceTo(v3())<0.001) {
                p.add(v3(0,0,-0.001));
            }
            return p;
        };

        settingRows.filter(row=>!row.disabled).forEach(row => {
            const parsed = this._parseRow(row, 'Light');

            if (row.Typ == 'HemisphereLight') {
                parsed.skyColor = parsed.color1;
                parsed.groundColor = parsed.color2;
            } else {
                parsed.color = parsed.color1;
            }

            const light = new parsed.Typ();
            lights.push(light);
            this._copyProps(light, parsed, key=>!['position','lookAt'].includes(key));
            light.position.copy(vectorBySelector(parsed.position));

            if (parsed.fixReflectionPoint) {
                lightgroup.add(light);
                if (light.target) {
                    lightgroup.add(light.target);
                }
                const plane = Maths3.plane.y0;
                light.onBeforeRenderStage = () => {
                    const raycaster = new THREE.Raycaster();
                    raycaster.setFromCamera(v3(), STAGE.camera);
                    const reflectionPoint = raycaster.ray.intersectPlane(plane);
                    if (reflectionPoint==null) {
                        return;
                    }
                    const camToReflection = reflectionPoint.clone().sub(STAGE.camera.position);
                    // camToReflection.normalize();

                    const reflected = camToReflection.clone().reflect(plane.normal);

                    const v4 = parsed.fixReflectionPoint;

                    reflected.applyEuler(new THREE.Euler(v4.x*Math.PI/180,v4.y*Math.PI/180,v4.z*Math.PI/180));

                    if (v4.w>0) {
                        reflected.normalize().multiplyScalar(v4.w);
                    }

                    const newLightPosition = reflected.add(reflectionPoint);

                    light.position.copy(newLightPosition);

                    light.updateMatrixWorld();

                    if (light.target == null) {
                        light.lookAt(reflectionPoint);
                    } else {
                        light.target.position.copy(reflectionPoint);
                    }
                }
            } else {
                (parsed.positionInCameraSpace ? cameraLightGroup : lightgroup).add(light);
                const lookAt = vectorBySelector(parsed.lookAt);
                if (light.target) {
                    light.target.position.copy(lookAt);
                    (parsed.lookAtInCameraSpace ? cameraLightGroup : lightgroup).add(light.target);
                } else {
                    if (!parsed.lookAtInCameraSpace && parsed.positionInCameraSpace) {
                        light.onBeforeRenderStage = () => {
                            const lookAtLocal = cameraLightGroup.worldToLocal(lookAt.clone()).sub(light.position);
                            light.lookAt(lookAtLocal);
                        };
                    } else {
                        light.lookAt(lookAt);
                    }
                }
                this.setLightShadowProperties(light, parsed);

                if (parsed.followCamera) {
                    const originalPosition = light.position.clone();
                    light.onBeforeRenderStage = () => {
                        if (light.target == null) {
                            light.lookAt(STAGE.orbitControl.target);
                        } else {
                            light.target.position.copy(STAGE.orbitControl.target);
                        }
                        light.position.copy(STAGE.camera.position).add(originalPosition);
                    };
                }
            }


            switch (row.Typ) {
                case "RectAreaLight":
                    light.width = parsed.AreaSize.x;
                    light.height = parsed.AreaSize.y;
                    break;
                case "SpotLight":
                    light.angle = Maths.cutBetween(0.1,parsed.angle,90) * Math.PI/180;
                case "PointLight":
                    if (STAGE.settings.renderer.pbrPipeline) {
                        light.decay = 2.0;
                        light.intensity = 1.0;
                        light.power = parsed.power;
                    }
                    break;
            }

            STAGE.maybeAddLightHelper(light);
        });

        STAGE.scene.add(lightgroup);
        STAGE.camera.add(cameraLightGroup);
        STAGE.scene.add(STAGE.camera);
        STAGE.dirty();
        return lights;
    }

    setLightShadowProperties(instance, parsed) {
        if (instance.shadow) {
            const shadowParsed = {};
            Object.keys(parsed).filter(k => k.startsWith('shadow'))
                .forEach(key => {
                    const targetkey = key[6].toLowerCase() + key.substr(7);
                    if (targetkey == 'camera') {
                        if (parsed[key] != null) {
                            instance.shadow.camera.near = parsed[key].x;
                            instance.shadow.camera.far = parsed[key].y;
                            instance.shadow.camera.fov = parsed[key].z;
                        }
                    } else if (targetkey == 'cameraSize') {
                        if (parsed[key] != null) {
                            instance.shadow.camera.left = parsed[key].x;
                            instance.shadow.camera.right = parsed[key].y;
                            instance.shadow.camera.top = parsed[key].z;
                            instance.shadow.camera.bottom = parsed[key].w;
                        }
                    } else {
                        shadowParsed[targetkey] = parsed[key];
                    }
                });
            this._copyProps(instance.shadow, shadowParsed);
        }
    }

    /** */
    getStandardEnvMap() {
        const row = APP.metadata.EnvMap?.find(x => x.Standard);
        if (!row) {
            return null;
        }
        return this.getEnvMap(row.name);
    }

    getStandardEnvMapName() {
        const row = APP.metadata.EnvMap?.find(x => x.Standard);
        if (!row) {
            return "";
        }
        return row.name;
    }

    /** */
     getEnvMapIntensity(name) {
        const row = APP.metadata.EnvMap?.find(x => x.name == name);
        if( !row) {
            return 1;
        }
        return row.envMapIntensity;
     }

    /** */
    getEnvMap(name) {
        const row = APP.metadata.EnvMap?.find(x => x.name == name);
        if (!row) {
            return null;
        }
        const cacheName = row.name + '_EnvMap';
        const fromCache = this.textureCache.get(cacheName);
        if (fromCache) {
            return fromCache;
        }
        let instance;
        if (Commons.isEmpty(row.image2)) {
            instance = this._loadTextureModified(row.image1);
            instance.mapping = row.Refraction ? EquirectangularRefractionMapping : THREE.EquirectangularReflectionMapping;
        } else {
            instance = this.loadTextureCube([row.image1, row.image2, row.image3, row.image4, row.image5, row.image6]);
            instance.mapping = row.Refraction ? THREE.CubeRefractionMapping : THREE.CubeReflectionMapping;
        }
        if (STAGE.settings.renderer.pbrPipeline) {
            instance.encoding = THREE.sRGBEncoding;
        }
        // instance.flipY = !row.flipY;
        this.textureCache.set(cacheName, instance);
        return instance;
    }

    /**
     * @param textureName
     */
    getTexture(textureName) {
        const fromCache = this.textureCache.get(textureName);
        if (fromCache) {
            return fromCache;
        }
        const row = APP.metadata.Texture.find(x => x.name == textureName);
        if (!row) {
            return null;
        }
        const parsed = this._parseRow(row, 'Texture');
        let instance;
        if (row.Typ == 'CubeTexture') {
            instance = this.loadTextureCube([parsed.image1, parsed.image2, parsed.image3, parsed.image4, parsed.image5, parsed.image6]);
        } else {
            instance = this._loadTextureModified(parsed.image1);
        }
        const filtered = this._filterProps(instance, parsed);
        this._copyProps(instance, filtered);
        this.textureCache.set(textureName, instance);
        return instance;
    }

    /**
     * wenn name leer ist, wird die Standard-Environment genommen (Standard=true)
     * @param name
     */
    setEnvironment(name=undefined) {
        const environmentRow = APP.richMetadata?.findEnvironment(name) || APP.metadata?.Environment?.find(e=>e.Standard);
        if (!environmentRow) {
            return;
        }

        if (STAGE.environment) {
            STAGE.scene.remove(STAGE.environment);
        }
        const environment = STAGE.environment = new THREE.Group();
        environment.name = 'Environment/'+environmentRow.name;
        this.environmentName = environmentRow.name;
        environment.isEnvironment = true;
        STAGE.scene.add(environment);

        if (environmentRow.Camera) {
            STAGE.setCameraFromDescription(environmentRow.Camera);
        }

        if (environmentRow.Light) {
            this.createLights(environmentRow.Light);
        }

        if (environmentRow.SkyMap) {
            const sky = this.createSky(environmentRow.SkyMap);
            environment.add(sky);
        }

        if (environmentRow.Ground) {
            const ground = this.createGround(environmentRow.Ground);
            environment.add(ground);
        }

        if (environmentRow.EnvMap) {
            this.setEnvMapIntoAllMaterials(environmentRow.EnvMap);
        }

        if (environmentRow.Model) {
            const modelRow = APP.richMetadata.findModel(environmentRow.Model);
            Models.loadModelPromise(modelRow).then(object3d=>environment.add(object3d));
        }

        if (environmentRow.Fog) {
            STAGE.scene.fog = new THREE.FogExp2( environmentRow.Fog.getHex(), environmentRow.FogPower );
        }

        if (STAGE.settings.renderer.pbrPipeline) {
            let exposure = STAGE.settings.renderer.exposure;
            if (environmentRow.Exposure) {
                exposure = environmentRow.Exposure;
            }
            STAGE.renderer.toneMappingExposure = exposure;
        }

        if (STAGE.settings.renderer.toneMapping) {
            let toneMappingName = STAGE.settings.renderer.toneMapping;
            if (environmentRow.ToneMapping) {
                toneMappingName = `${environmentRow.ToneMapping}ToneMapping`
            }
            STAGE.renderer.toneMapping = THREE[toneMappingName]
        }



        return environment;
    }

    createSky(envMapName) {
        const skyMap = this.getEnvMap(envMapName);
        const isEquirectangular = skyMap.mapping == THREE.EquirectangularRefractionMapping || skyMap.mapping ==THREE.EquirectangularReflectionMapping;
        const skyMaterial = new THREE.MeshBasicMaterial( { map: skyMap, side: THREE.FrontSide, fog: false } );
        const radius = Math.min(STAGE.settings.sky_radius,STAGE.camera.far * 0.9);
        const geometry = isEquirectangular
            ? new THREE.SphereBufferGeometry( radius, 48, 24 )
            :new THREE.BoxBufferGeometry( radius * 2, radius * 2, radius * 2 );
        // ohne die Scale z=-1 wäre die EnvMap vs. SkyMap invertiert
        geometry.scale( 1, 1, -1 );
        const sky = new THREE.Mesh( geometry, skyMaterial );
        sky.name = 'sky';
        sky.raycastVisibleTags = [];
        sky.frustumCulled = false;
        sky.onBeforeRenderStage = ()=>{
            const cameraPosition = STAGE.camera.getWorldPosition();
            sky.position.copy(cameraPosition).add(v3(0.0001,0.0001,0.0001));
        };
        return sky;
    }

    setEnvMapIntoAllMaterials(envMapName) {
        const envMap = this.getEnvMap(envMapName);
        if (!envMap) {
            return;
        }
        STAGE.scene.environment=envMap;
    }

    createGround(name) {
        const material = UniversalLoader.getMaterialFactory().create(name);
        const radius = STAGE.settings.sky_radius;
        const geometry = new CircleUvBufferGeometry( radius, 48 );
        const ground = new THREE.Mesh( geometry, material );
        ground.name = 'ground';
        ground.raycastVisibleTags = [];
        ground.quaternion.setFromUnitVectors(Maths3.vector.z, Maths3.vector.y);
        ground.position.set(0,-0.01,0);
        ground.receiveShadow=STAGE.settings.shadows.enabled;
        return ground;
    }

    _loadTextureModified(url) {
        return this.loadTexture(url, (texture) => {
            if (texture.useRepeat) {
                const repeatX = texture.repeat.x;
                const repeatY = texture.repeat.y * texture.image.width / texture.image.height;
                texture.repeat.set(repeatX, repeatY);
            }

            if (this.useLowResTextures) {
                console.error('usage of makeLowResImage discouraged!!');
                this.makeLowResImage(texture);
            }
            if (this.useMinAnisotropy) {
                console.error('usage of useMinAnisotropy discouraged!!');
                this.makeMinAnisotropy(texture);
            } else {
                texture.anisotropy = Textures.getMaxAnisotropy();
            }
        });
    }

    makeLowResImage(texture) {
        if (!texture.image) {
            return;
        }
        texture.highResImage = texture.image;
        texture.image = Images.scaleDown(texture.image, 0.5);
    }

    makeMinAnisotropy(texture) {
        texture.originalAnisotropy = texture.anisotropy;
        texture.anisotropy = 1;
    }

    /**
     * @private
     */
    _createMaterial(materialName) {
        const row = APP.metadata.Material?.find(x => x.name == materialName);
        if (!row) {
            return this._createMaterialPbr(materialName);
        }
        return this._createMaterialFromRow(row);
    }

    /** */
    _createMaterialFromRow(row) {
        const parsed = this._parseRow(row, 'Material');
        let type = parsed.Typ;
        if (this.useLambertInsteadOfPhong && type === THREE.MeshPhongMaterial) {
            type = THREE.MeshLambertMaterial;
        }
        if (type === THREE.MeshBasicMaterial) {
            parsed.lights = false;
        }
        const filtered = this._filterProps(new type(), parsed);
        // console.log(row,parsed,filtered);
        const instance = new type(filtered);
        // console.debug(materialName,filtered,instance);
        return instance;
    }

    /**
     * @private
     */
    _createMaterialPbr(materialName) {
        const row = APP.metadata.MaterialPbr?.find(x => x.name == materialName);
        return this._createMaterialPbrFromRow(row);
    }

    _createMaterialPbrFromRow(row) {
        if (!row) {
            return null;
        }

        const parsed = this._parseRow(row, 'MaterialPbr');

        let material = {};
        let mapCache = {};

        // auch im Falle von pbrMap muss die ursprüngliche Map wie aoMap vorhanden sein - man kann nicht einfach davon
        // ausgehen, dass für PBR immer alle Maps vorhanden sind, die metalnessMap könnte man auch weglassen.
        // Aus der kombinierten PBR-Map ist diese Info nicht mehr rekonstruierbar.
        const pbrMergedMaps = ['roughnessMap','metalnessMap','aoMap'];

        const loadMap = mapName => {

            let path = "";
            if (STAGE.settings.renderer.usePbrMap && !Commons.isEmpty(parsed.pbrMap) && pbrMergedMaps.includes(mapName)) {
                path = row['pbrMap'];
            } else {
                path = row[mapName];
            }
            if (Commons.isEmpty(path)) {
                return;
            }

            const fromCache = mapCache[path];
            const map = fromCache || this._loadTextureModified(path);

            const isColorMap = mapName == 'colorMap';
            material[isColorMap ? 'map' : mapName] = map;

            if (STAGE.settings.renderer.pbrPipeline && isColorMap && fromCache == null) {
                map.encoding = THREE.sRGBEncoding;
            }

            map.wrapS = map.wrapT = parsed.wrap == 'Mirrored' ? THREE.MirroredRepeatWrapping : THREE.RepeatWrapping;

            const repeatX = (parsed.repeat?.x ? parsed.repeat.x : 1) / parsed.scale;
            const repeatY = (parsed.repeat?.y ? parsed.repeat.y : 1) / parsed.scale;
            if (!/alphaMap|aoMap|lightMap/.test(mapName)) {
                map.repeat.set(repeatX, repeatY);
                map.useRepeat = parsed.repeat != undefined;
            } else {
                map.repeat.set(1, 1);
                material.transparent = true;
            }
            mapCache[path] = map;
        };

        if (parsed.affectedByLight) {

            ['colorMap', 'alphaMap', 'normalMap', 'envMap', 'roughnessMap', 'metalnessMap', 'displacementMap', 'emissiveMap', 'aoMap'].forEach(loadMap);
            if (parsed.normalMap && parsed.normalScale) {
                material.normalScale = v2(parsed.normalScale, parsed.normalScale);
            }
            if (parsed.displacementMap && parsed.displacementScale) {
                material.displacementScale = parsed.displacementScale;
            }
            // im Falle von gesetzten Maps sollten beide Werte in der Tabelle auf 1 stehen
            material.roughness = parsed.roughness;
            material.metalness = parsed.metalness;
        } else {
            ['colorMap', 'alphaMap'].forEach(loadMap);
        }

        material.envMapIntensity = this.getEnvMapIntensity(this.getStandardEnvMapName()) || 0;

        if (!Commons.isEmpty(parsed.alpha) && parsed.alpha<0.9999) {
            material.opacity = parsed.alpha;
            material.transparent = true;
        }

        if (!Commons.isEmpty(parsed.alphaTest) && parsed.alphaTest>0.0001) {
            material.alphaTest = parsed.alphaTest;
        }

        if (!Commons.isEmpty(row.aoMap)) {
            // die AOMap bezieht sich auf ein 2. Set von UV-Koordinaten und wird daher nicht skaliert
            material.aoMapIntensity = row.aoMapIntensity;
        }

        if (!Commons.isEmpty(row.color)) {
            material.color = new THREE.Color(parsed.color);
            if (STAGE.settings.renderer.pbrPipeline) {
                material.color.convertSRGBToLinear();
            }
        } else {
            material.color = null;
        }
        material.side = parsed.side;
        material.name = parsed.name;

        if (parsed.affectedByLight) {
            material = new THREE.MeshStandardMaterial(material);
        } else {
            material = new THREE.MeshBasicMaterial(material);
        }
        material.flatShading = false;

        return material;
    }

    /**
     * @private
     */
    _filterProps(instance, parsed) {
        const filtered = {};
        _.keys(instance).forEach(prop => {
            let value = parsed[prop];
            if (value != null && instance[prop] != value) {
                if (value instanceof THREE.Color) {
                    value = value.getHex();
                }
                filtered[prop] = value;
            }
        });
        return filtered;
    }

    /**
     * @private
     */
    _copyProps(instance, parsed, acceptProp=(key)=>true) {
        _.keys(instance).forEach(prop => {
            if (acceptProp(prop)) {
                let value = parsed[prop];
                if (value != null && instance[prop] != value) {
                    if (instance[prop]?.copy) {
                        instance[prop].copy(value);
                    } else {
                        instance[prop] = value;
                    }
                }
            }
        });
    }

    /**
     * @private
     */
    _parseRow(row, tableName) {
        const metaTable = this._getTable(tableName);
        const parsed = {};
        metaTable.columns.forEach(column => {
            if (column.readOnly) {
                return;
            }
            const name = column.name;
            let value = row[name];

            if (value == null) {
                parsed[name] = null;
                return;
            }
            if (column.foreignKey == 'Texture.name') {
                parsed[name] = this.getTexture(value);
                return;
            }
            if (name == 'anisotropy') {
                parsed[name] = value == 'auto' ? Textures.getMaxAnisotropy() : parseInt(value, 10);
                return;
            }
            switch (column.type) {
                case 'String':
                    if (column.selectOptions && THREE[value] !== undefined) {
                        parsed[name] = THREE[value];
                    } else if (Commons.isEmpty(value)) {
                        // das hier ist zumindest für den Materialparameter precision auf iPads notwendig
                        parsed[name] = undefined;
                    } else {
                        parsed[name] = value;
                    }
                    break;
                default:
                    parsed[name] = value;
                    break;
            }
        });
        return parsed;
    }

    /**
     * @private
     */
    _getTable(name) {
        return APP.metamodel.tables.find(table => table.name == name);
    }

}
